import { useState, useEffect, ReactElement, useRef } from "react";
import styled from "styled-components";
import { primaryActive, primaryInactive } from "../../typography/alpha";

const StyledNavbar = styled.div`
  && {
    position: relative;
    display: inline-flex;
    width: 100%;
    padding-left: 8px;
    border-bottom: 1px solid var(--color-primary-100);
    max-height: 40px;
    box-sizing: border-box;
  }
`;

const Tab = styled.button`
  && {
    position: relative;
    cursor: pointer;
    box-sizing: border-box;
    min-height: 32px;
    line-height: 16px;
    margin: 0px 12px;
    overflow: hidden;
    padding: 12px 12px 8px 12px;
    text-align: center;
    ${primaryInactive.body1};
    color: var(--color-primary-800);
    background: var(--color-neutral-000-full);
    border: none;
    border-bottom: 4px solid var(--color-neutral-000-full);
    :focus {
      outline: none;
    }
    :hover,
    :focus {
      &:after {
        transform: scaleX(1);
      }
    }

    &.active {
      cursor: default;
      ${primaryActive.subtitle3};
    }
  }
`;

const ActiveUnderline = styled.div`
  position: absolute;
  bottom: 0;
  height: 4px;
  background: var(--color-secondary-800);
  transition: all 0.3s ease-in-out;
`;

const useActiveUnderlineStyles = (activeTab = 0) => {
  const refs = useRef<HTMLButtonElement[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dynamically built style object
  const [styles, setStyles] = useState<any>({});

  useEffect(() => {
    const element = refs.current?.[activeTab];
    if (element) {
      setStyles({
        left: `${element.offsetLeft}px`,
        width: `${element.offsetWidth}px`,
      });
    }
  }, [activeTab]);

  return { refs, styles };
};

type CustomTabProps = {
  id: string;
  component: ReactElement | ((props: { tabIsActive: boolean }) => ReactElement);
};

type TabProps =
  | { label: string; id?: string; onClick: () => void }
  | CustomTabProps;

type SubNavbarProps = {
  tabs: TabProps[];
  index?: number;
  className?: string;
  id?: string;
};

export const SubNavbar = (props: SubNavbarProps) => {
  const [activeTabIndex, setActiveTabIndex] = useState(
    props.index ? props.index : 0,
  );
  const { refs, styles } = useActiveUnderlineStyles(activeTabIndex);

  useEffect(() => {
    setActiveTabIndex(props.index ? props.index : 0);
  }, [props.index]);

  return (
    <StyledNavbar className={props?.className}>
      {props.tabs.map((tab, index) => {
        const tabIsActive = index === activeTabIndex;
        const key = "label" in tab ? tab.label : tab.id;
        const onClick = () => {
          if (tabIsActive) return;

          setActiveTabIndex(index);
          if ("onClick" in tab) tab.onClick();
        };
        const tabContent =
          "label" in tab
            ? tab.label
            : typeof tab.component === "function"
              ? tab.component({
                  tabIsActive,
                })
              : tab.component;
        return (
          <Tab
            data-testid={`sub-navbar-tab-${
              "label" in tab ? tab.label : tab.id
            }`}
            id={`sub-navbar-tab-${tab?.id}`}
            key={key}
            ref={(el) => refs?.current?.push(el as HTMLButtonElement)}
            onClick={onClick}
            tabIndex={index}
            className={tabIsActive ? "active" : ""}
          >
            {tabContent}
          </Tab>
        );
      })}
      <ActiveUnderline style={styles} />
    </StyledNavbar>
  );
};
